import { api, ApiError, type BillingHistoryEntry } from '../api.ts';
import { el, formatDate, mount, onTap } from '../ui/dom.ts';
import { iconEl } from '../ui/icons.ts';
import { appMain, ensureAppShell, pageIntro } from '../ui/shell.ts';
import { errorNote, skeletonRows, stateBlock, toast } from '../ui/feedback.ts';
import { navigate, renderGuard } from '../router.ts';
import { loadSession, state } from '../state.ts';
import { t } from '../i18n/index.ts';
import type { BillingPeriod, PlansPayload } from './plans-section.ts';

// Fatura bilgileri: iyzico aboneliği kişiye açar ve ad, soyad, kimlik numarası,
// telefon ve adresi zorunlu tutar. Bilgiler yalnız iyzico'ya iletilir; sunucu
// saklamaz. Kart bilgisi bu ekranda istenmez: sonraki adım iyzico'nun formudur.

function field(label: string, input: HTMLElement, hint?: string): HTMLElement {
  const id = 'f_' + Math.random().toString(36).slice(2, 9);
  input.id = id;
  return el('div', { class: 'field' }, el('label', { for: id }, label), input, hint ? el('span', { class: 'hint' }, hint) : null);
}

/** Ödeme adımları: kullanıcı nerede olduğunu ve kart bilgisinin hangi adımda,
 *  kimin formuna girileceğini baştan görür. */
function steps(active: 1 | 2 | 3 = 1): HTMLElement {
  const item = (n: 1 | 2 | 3, label: string) => el('li', n === active ? { class: 'on', 'aria-current': 'step' } : n < active ? { class: 'done' } : {},
    el('span', {}, n < active ? iconEl('check') : String(n)), label);
  return el('ol', { class: 'pay-steps', 'aria-label': t('bill.stepsLabel') },
    item(1, t('bill.step1')), item(2, t('bill.step2')), item(3, t('bill.step3')));
}

function assurance(icon: 'lock' | 'check' | 'card' | 'info', text: string): HTMLElement {
  return el('li', {}, iconEl(icon), el('span', {}, text));
}

export async function checkoutDetailsView(_params: Record<string, string>, query: URLSearchParams): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const planCode = query.get('plan') ?? '';
  const period: BillingPeriod = query.get('period') === 'yearly' ? 'yearly' : 'monthly';
  const stale = renderGuard();

  let data: PlansPayload;
  try {
    data = await api.get<PlansPayload>('/api/billing/plans');
  } catch (err) {
    if (stale()) return;
    mount(host, el('div', { class: 'wrap' }, pageIntro(t('bill.title')), stateBlock({ title: t('plans.page.loadFail'), body: (err as Error).message })));
    return;
  }
  if (stale()) return;
  const plan = data.plans.find((p) => p.code === planCode && p.monthlyMinor > 0);
  if (!plan || !data.payments.enabled || data.payments.provider !== 'iyzico') { navigate('/plans', true); return; }

  const price = period === 'yearly' ? plan.yearlyLabel ?? plan.renewLabel : plan.renewLabel;
  const summary = plan.trialDays
    ? `${t('plans.renew.trial', { n: plan.trialDays })} ${t('plans.renew.trialNote', { price })}`
    : price;

  const name = el('input', { type: 'text', autocomplete: 'given-name', maxlength: '60', required: true });
  const surname = el('input', { type: 'text', autocomplete: 'family-name', maxlength: '60', required: true });
  const identity = el('input', { type: 'text', inputmode: 'numeric', autocomplete: 'off', maxlength: '11', pattern: '[0-9]{11}', required: true });
  const phone = el('input', { type: 'tel', autocomplete: 'tel', maxlength: '20', placeholder: t('bill.phonePh'), required: true });
  const city = el('input', { type: 'text', autocomplete: 'address-level1', maxlength: '60', required: true });
  const address = el('input', { type: 'text', autocomplete: 'street-address', maxlength: '240', required: true });

  const problem = el('div', {});
  // Ödeme öncesi onay: koşullar, hizmetin hemen başlaması ve iade yapılmaması.
  const consent = el('input', { type: 'checkbox', required: true }) as HTMLInputElement;
  const terms = el('a', { href: '/legal?section=subscription', target: '_blank', rel: 'noopener' }, t('bill.consentLink'));
  const consentRow = el('label', { class: 'pay-consent' }, consent, el('span', {}, terms, ' ', t('bill.consent')));
  const submit = el('button', { class: 'btn btn-light', type: 'submit' }, t('bill.submit'));
  const cancel = el('button', { class: 'btn btn-quiet', type: 'button' }, t('common.cancel'));
  onTap(cancel, () => navigate('/plans'));

  const form = el('form', { class: 'panel pay-form' },
    el('h2', {}, t('bill.formTitle')),
    el('p', {}, t('bill.lead')),
    el('div', { class: 'pay-row' }, field(t('bill.name'), name), field(t('bill.surname'), surname)),
    el('div', { class: 'pay-row' },
      field(t('bill.identity'), identity, t('bill.identityHint')),
      field(t('bill.phone'), phone)),
    el('div', { class: 'pay-row pay-row-address' }, field(t('bill.city'), city), field(t('bill.address'), address)),
    consentRow,
    problem,
    el('div', { class: 'modal-actions' }, cancel, submit),
    el('p', { class: 'pay-secure' }, iconEl('lock'), el('span', {}, t('bill.cardNote'))),
  );

  const dueToday = plan.trialDays ? t('bill.dueTrial') : price;
  const aside = el('aside', { class: 'panel pay-summary', 'aria-label': t('bill.summaryTitle') },
    el('h2', {}, t('bill.summaryTitle')),
    el('dl', {},
      el('div', {}, el('dt', {}, t('bill.sumPlan')), el('dd', {}, plan.name)),
      el('div', {}, el('dt', {}, t('bill.sumPeriod')), el('dd', {}, period === 'yearly' ? t('bill.yearly') : t('bill.monthly'))),
      plan.trialDays
        ? el('div', {}, el('dt', {}, t('bill.sumAfterTrial', { n: plan.trialDays })), el('dd', {}, price))
        : null,
      el('div', { class: 'total' }, el('dt', {}, t('bill.sumToday')), el('dd', {}, dueToday))),
    el('p', { class: 'pay-note' }, summary, ' ', t('plans.fine.vat')),
    data.payments.sandbox
      ? el('p', { class: 'pay-note' }, el('span', { class: 'tag-soft tag-test' }, 'TEST'), ' ', t('plans.fine.sandbox'))
      : null,
    el('ul', { class: 'pay-assure' },
      assurance('lock', t('bill.assureCard')),
      assurance('card', t('bill.assureProvider')),
      // Abonelik ürünü banka kartıyla çalışmıyor: kart girildikten sonra
      // reddedilmek yerine burada söylenir.
      assurance('card', t('bill.assureCreditCard')),
      assurance('check', t('bill.assureCancel')),
      assurance('info', t('bill.assureNoRefund'))),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const customer = {
      name: name.value, surname: surname.value, identityNumber: identity.value.replace(/\D/g, ''),
      gsmNumber: phone.value, city: city.value, address: address.value,
    };
    if (customer.identityNumber.length !== 11 || customer.gsmNumber.replace(/\D/g, '').length < 10) {
      mount(problem, errorNote(t('bill.invalid')));
      return;
    }
    if (!consent.checked) {
      mount(problem, errorNote(t('bill.consentRequired')));
      return;
    }
    submit.disabled = true;
    submit.textContent = t('bill.opening');
    mount(problem, '');
    try {
      const checkout = await api.post<{ redirectUrl: string }>('/api/billing/checkout', { planCode, period, customer, consent: true });
      // iyzico'nun betiği uygulamanın içerik politikasında çalışamaz; ödeme
      // sunucunun ayrı sayfasında açılır (tam sayfa geçişi).
      location.assign(checkout.redirectUrl);
    } catch (err) {
      mount(problem, errorNote(err instanceof ApiError ? err.message : t('plans.checkout.fail')));
      submit.disabled = false;
      submit.textContent = t('bill.submit');
    }
  });

  mount(host, el('div', { class: 'wrap' }, pageIntro(t('bill.title')), steps(), el('div', { class: 'pay-layout' }, form, aside)));
  name.focus();
}

// ============================ Satın alma onayı ==============================
// Ödeme adımları "3 Onay" diyordu ama böyle bir ekran yoktu: müşteri Paketler
// ekranına dönüp altı saniyelik bir bildirim görüyordu. Burada ne aldığını, bugün
// ne ödediğini ve bir sonraki tahsilatın ne zaman, ne kadar olacağını görür.

/** Onay ekranı satın almadan sonra bu süre boyunca gösterilir. */
const DONE_WINDOW_MS = 60 * 60_000;

function doneRow(label: string, value: string, total = false): HTMLElement {
  return el('div', total ? { class: 'total' } : {}, el('dt', {}, label), el('dd', {}, value));
}

export async function checkoutDoneView(): Promise<void> {
  ensureAppShell();
  const host = appMain();
  const stale = renderGuard();
  mount(host, el('div', { class: 'wrap' }, pageIntro(t('done.title')), steps(3), el('div', { class: 'panel pay-done' }, skeletonRows(3))));

  // Ödeme sunucuda işlendi; oturum (paket, abonelik özeti) tazelenir.
  let events: BillingHistoryEntry[] = [];
  let mailEnabled = false;
  let loaded = false;
  // Süre sunucu saatine göre ölçülür: cihazın saati yanlışsa onay kaybolmasın.
  let serverNow = Date.now();
  try {
    await loadSession();
    const history = await api.get<{ events: BillingHistoryEntry[]; mailEnabled?: boolean; now?: number }>('/api/billing/history');
    events = history.events;
    mailEnabled = !!history.mailEnabled;
    if (history.now) serverNow = history.now;
    loaded = true;
  } catch { /* aşağıda: ayrıntı çizilemez ama satın alma bildirilir */ }
  if (stale()) return;
  // Kayıt alınamadı (ağ hatası): ödeme yapmış müşteri sessizce Paketler'e düşmesin.
  if (!loaded) {
    if (state.session && state.session.entitlements.effectivePlan !== 'free') toast(t('pay.success'), 'ok', 6000);
    navigate('/plans', true);
    return;
  }

  const session = state.session;
  const ent = session?.entitlements;
  const sub = session?.subscription;
  // Yalnız az önce tamamlanan satın alma onaylanır. Sayfa günler sonra (yer imi,
  // geri tuşu) açılırsa "deneme süreniz başladı" demek yanlış olurdu: arada
  // yenileme ya da iptal olmuş olabilir.
  const latest = events.find((e) => e.kind === 'trial_started' || e.kind === 'purchase' || e.kind === 'renewal');
  const purchase = latest && latest.kind !== 'renewal' && serverNow - latest.created_at < DONE_WINDOW_MS ? latest : undefined;
  if (ent && ent.effectivePlan !== 'free' && !purchase) { navigate('/plans', true); return; }

  // Adres elle açıldıysa ya da ödeme tamamlanmadıysa: boş bir "tebrikler" çizilmez.
  if (!ent || ent.effectivePlan === 'free' || !purchase) {
    const go = el('button', { class: 'btn btn-light', type: 'button' }, t('done.toPlans'));
    onTap(go, () => navigate('/plans', true));
    mount(host, el('div', { class: 'wrap' }, pageIntro(t('done.noneTitle')), stateBlock({ title: t('done.noneTitle'), body: t('done.noneBody') }), go));
    return;
  }

  const trial = purchase.kind === 'trial_started';
  const period = sub?.period ?? purchase.period;
  const toPlans = el('button', { class: 'btn btn-outline', type: 'button' }, t('done.manage'));
  onTap(toPlans, () => navigate('/plans'));
  const watch = el('button', { class: 'btn btn-light', type: 'button' }, iconEl('play'), t('done.watch'));
  onTap(watch, () => navigate('/browse'));

  const panel = el('div', { class: 'panel pay-done' },
    el('div', { class: 'pay-done-mark' }, iconEl('check')),
    el('h2', {}, t('done.title')),
    el('p', {}, t(trial ? 'done.leadTrial' : 'done.leadPaid', { plan: ent.planName })),
    el('dl', { class: 'pay-done-facts' },
      doneRow(t('bill.sumPlan'), ent.planName),
      period ? doneRow(t('bill.sumPeriod'), period === 'yearly' ? t('bill.yearly') : t('bill.monthly')) : null,
      doneRow(t('done.paidToday'), trial ? t('bill.dueTrial') : purchase.amount_label, true),
      sub?.nextChargeAt
        ? doneRow(t(sub.inTrial ? 'sub.trialEnds' : 'sub.nextCharge'), t('sub.chargeValue', { date: formatDate(sub.nextChargeAt), price: sub.priceLabel ?? '' }))
        : null,
      doneRow(t('done.refLabel'), purchase.ref),
    ),
    purchase.test ? el('p', { class: 'pay-note' }, el('span', { class: 'tag-soft tag-test' }, 'TEST'), ' ', t('plans.fine.sandbox')) : null,
    mailEnabled && session ? el('p', { class: 'pay-note' }, t('done.mail', { email: session.user.email })) : null,
    el('p', { class: 'pay-note' }, t('done.cancelNote')),
    el('div', { class: 'modal-actions' }, toPlans, watch),
  );
  mount(host, el('div', { class: 'wrap' }, pageIntro(t('done.title')), steps(3), panel));
}
